import React, { useState, useEffect, useRef, useContext } from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { GatsbyImage, getImage } from "gatsby-plugin-image"
import Tabs from 'components/common/tabs'
import Pagination from 'components/common/pagination'
import { detectMobileAndTablet, isSSR } from "utils"
import ContentWrapper from "styles/contentWrapper"

const starOn = "https://hrflow-ai.imgix.net/star-on.svg"
const starOff = "https://hrflow-ai.imgix.net/star-off.svg"

const StyledSection = styled.section`
  width: 100%;
  height: auto;
  padding: 3rem 0;
`

const StyledContentWrapper = styled(ContentWrapper)`
  && {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: 0;
    padding-left: 0;
    @media (min-width: ${({ theme }) => theme.breakpoints.xs}) {
      padding-right: 1rem;
      padding-left: 1rem;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding-right: 2.5rem;
      padding-left: 2.5rem;
    }
    .section-title {
      color: ${({ theme }) => theme.colors.primary};
      text-align: center;
      margin-top: 0;
      margin-bottom: 3rem;
      padding: 0 1rem;
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        padding-right: 0;
        padding-left: 0;
      }
    }

    .tabs { 
      display: flex;
      flex-direction: column;
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        flex-direction: row;
      }
    }

    .tab-list {
      margin-top: 1.5rem;
      @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        display: grid;
        -webkit-mask-image: linear-gradient(to right,black 80%,transparent 100%);
        grid-template-columns: repeat(7,10rem);
        grid-template-rows: repeat(1,auto);
        grid-auto-flow: column;
        column-gap: 1rem;
        row-gap: 1rem;
        padding: 0 2.5rem 0 2.5rem;
        overflow-x: scroll;
        overflow-y: hidden;
        margin-top: 2rem;
        &::-webkit-scrollbar {
          display: none;
        }
        @media (hover: hover) {
          scrollbar-color: ${({ theme }) => theme.colors.scrollBar} transparent; // Firefox only
          &::-webkit-scrollbar {
            display: block;
            -webkit-appearance: none;
          }
      
          &::-webkit-scrollbar:horizontal {
            height: 0.8rem;
          }
      
          &::-webkit-scrollbar-thumb {
            border-radius: 8px;
            border: 0.2rem solid ${({ theme }) => theme.colors.background};
            background-color: ${({ theme }) => theme.colors.scrollBar};
          }
      
          &::-webkit-scrollbar-track {
            background-color: ${({ theme }) => theme.colors.background};
            border-radius: 8px;
          }
        }
      }
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        flex-direction: column;
      }
    }

    .tab-list-item {
      background: #fff;
      border: .1rem solid #bddae3;
      box-sizing: border-box;
      border-radius: 10rem;
      height: 3.2rem;
      width: 10rem;
      margin: 0.5rem 3rem 0 0;
      display: flex;
      justify-content: center;
      align-items: center;
      font-size: 1rem;
      text-align: center;
      letter-spacing: -.05rem;
      color: ${({ theme }) => theme.colors.primary};
      font-weight: 600;
      &::before {
        display: none;
      }
      &:hover, &.tab-list-active {
        background: ${({ theme }) => theme.colors.primary}!important;
        border: .1rem solid ${({ theme }) => theme.colors.primary}!important;
        color: #fff!important;
        -webkit-transition: all .3s ease-in;
        transition: all .3s ease-in;
        text-decoration: none;
      }
    }
  }
`
const StyledItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  min-height: 41.2rem;
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    justify-content: center;
  }
  .item {
    padding: 1.6rem;
    margin-left: 1rem;
    margin-bottom: 1rem;
    height: 19.6rem;
    border: 2px solid #bddae3;
    background-color: #fff;
    border-radius: 1.5rem;
    display: flex;
    width: 23%;
    min-width: 250px;
    max-width: 300px;
    cursor: pointer;
    .item__inner {
      width: 100%;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      .logo-wrapper {
        display: flex;
        justify-content: center;
        margin-bottom: 5rem;
        margin-top: 3rem;
        .logo {
          width: 70px;
          height: auto;
        }
      }
      .title {
        ine-height: normal;
        font-size: 1.5rem;
        text-align: center;
        letter-spacing: -.05rem;
        margin-top: .7rem;
        margin-bottom: 3.5rem;
        font-weight: 600;
      }
      .reviews {
        display: flex;
        justify-content: center;
        img {
          margin: 0.3rem;
        }
      }
    }
    .item__inner.hovred {
      display: none;
    }

    &:hover {
      background: linear-gradient(0deg, #21acc5, #21acc5);
      opacity: .95;
      border: none;
      color: white;
      .item__inner {
        display: none;
      }

      .item__inner.hovred {
        display: flex;
        flex-direction: column;
        .title {
          text-align: left;
          margin-bottom: 1rem;
          width: 100%;
        }
        p {
          margin-bottom: 0;
        }
        .logo-wrapper-mini {
          display: flex;
          justify-content: flex-end;
          height: 100%;
          width: 100%;
          align-items: flex-end;
          .wrapper {
            background: white;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 50px;
            height: 50px;
            border-radius: 50%;
            .logo {
              width: 30px;
              height: auto;
              object-fit: unset;
            }
          }
        }
      }
    }
  }
`

const TabContent = props => {
  return (
    <>
      <StyledItems>
        {
          props.items.map(({logo, review, description, label}, key) => {
            return (
              <div className="item" key={`${label}-${key}`}>
                <div className="item__inner">
                  <div className="logo-wrapper">
                    <GatsbyImage className="logo" image={getImage(logo)}  alt={label}/>
                  </div>
                  <div className="title">{label}</div>
{/*                   <div className="reviews">
                    {[0, 1, 2, 3, 4].map((_, index) => <img alt="start icon" key={index} src={index < review ? starOn: starOff} />)}
                  </div> */}
                </div>
                <div className="item__inner hovred">
                  <div className="title">HrFlow.ai + {label}</div>
                  <p>{description}</p>
                  <div className="logo-wrapper-mini">
                    <div className="wrapper">
                      <GatsbyImage className="logo" image={getImage(logo)}  alt={label}/>
                    </div>
                  </div>
                </div>
              </div>
            )
          })
        }
      </StyledItems>
      {props.maxPage > 1 ?  <Pagination currentPage={props.currentPage} maxPage={props.maxPage} changePage={props.changePage} /> : ''}
    </>
  )
}

const Explore = ({ content }) => {
  const { exports, frontmatter } = content[0].node
  const { items } = exports
  const [page, setPage] = useState(1)
  const [stateItems, setStateItems] = useState(items)
  const [maxPage, setMaxPage] = useState(0)
  const [tab, setTab] = useState("All")

  useEffect(() => {
    let newItems = [...items]
    setPage(1)
    if (tab !== "All") {
      newItems = newItems.filter(item => item.category === tab)
    }
    const maxPage = Math.ceil(newItems.length/8)
    setMaxPage(maxPage)
    newItems = newItems.slice(0, 8)
    setStateItems(newItems)
  }, [tab])
  

  const tabChanged = value => {
    setTab(value)
  }

  const onChangePage = (val) => {
    let newItems = [...items]
    if (tab !== "All") {
      newItems = newItems.filter(item => item.category === tab)
    }
    newItems = newItems.slice((val - 1)*8, 8*val)
    setStateItems(newItems)
    setPage(val);
  }

  return (
    <StyledSection id="Explore">
      <StyledContentWrapper>
        <h2 className="section-title">{frontmatter.title}</h2>
        <Tabs tabChanged={tabChanged} minHeight="unset" maxWidth maxHeight="unset">
          <div label="All">
            <TabContent category="All" items={stateItems} currentPage={page} maxPage={maxPage} changePage={onChangePage} />
          </div>
          <div label="Messaging">
              <TabContent category="Messaging" items={stateItems} currentPage={page} maxPage={maxPage} changePage={onChangePage} />
          </div>
          <div label="Chats">
            <TabContent category="Chats" items={stateItems} currentPage={page} maxPage={maxPage} changePage={onChangePage} />
          </div>
          <div label="Forms">
            <TabContent category="Forms" items={stateItems} currentPage={page} maxPage={maxPage} changePage={onChangePage} />
          </div>
          <div label="Cloud Storage">
            <TabContent category="Cloud Storage" items={stateItems} currentPage={page} maxPage={maxPage} changePage={onChangePage} />
          </div>
          <div label="API">
            <TabContent category="API" items={stateItems} currentPage={page} maxPage={maxPage} changePage={onChangePage} />
          </div>
          <div label="Jobboards">
            <TabContent category="Jobboards" items={stateItems} currentPage={page} maxPage={maxPage} changePage={onChangePage} />
          </div>
        </Tabs>
      </StyledContentWrapper>
    </StyledSection>
  )
}



export default Explore
